import { History } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../../../components/ui/card'
import { Switch } from '../../../../../components/ui/switch'
import { format } from 'date-fns'
import { Table } from '../../../../../../app/entities/Table'
import { cn } from '../../../../../../app/utils/cn'
import { SettingsTable } from './components/SettingsTable'
import { useTableCardController } from './useTableCardController'
import { FormProvider } from 'react-hook-form'
import {
  AlertDialog,
  AlertDialogTrigger,
} from '../../../../../components/ui/alert-dialog'
import { EnableAndDisableCard } from './components/EnableAndDisableCard'
import { formatPercentage } from '@/app/utils/comissionPercentage'
import { formatMoneyValue } from '@/app/utils/formatMoneyValue'
import { useAuth } from '@/app/hooks/useAuth'
import { useMemo } from 'react'

interface TableItemProps {
  label: string
  value: string
}

const TableItem = ({ label, value }: TableItemProps) => {
  return (
    <div>
      <p className="font-semibold text-black04">{label}</p>
      <p className="text-xl font-semibold">{value}</p>
    </div>
  )
}

export const TableCard = (table: Table) => {
  const { methods, setSelectedTableCard } = useTableCardController()
  const { isSuperAdmin } = useAuth()

  const handleSettingsClick = () => {
    setSelectedTableCard(table)
  }

  const items = useMemo(() => {
    const isCLT = table.type === 'CLT'
    const res = [
      {
        label: 'Comissão Vendedor',
        value: formatPercentage(table.percents * 100) + '%',
      },
      ...(isCLT
        ? []
        : [
            {
              label: 'Bônus',
              value: table?.bonus
                ? formatPercentage(table.bonus * 100) + '%'
                : '-',
            },
          ]),
      {
        label: 'Comissão Líder',
        value: formatPercentage((table?.leaderCommission ?? 0) * 100) + '%',
      },
    ]

    if (isSuperAdmin)
      res.push(
        {
          label: 'Limite de emissão',
          value: table?.limit ? formatMoneyValue(table.limit) : '-',
        },
        {
          label: 'Limite de comissão',
          value: table?.maxPercents
            ? formatPercentage(table.maxPercents * 100) + '%'
            : '-',
        },
      )

    return res
  }, [isSuperAdmin, table])

  return (
    <AlertDialog>
      <Card className={cn('w-full', !table.isActive && 'text-black04')}>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="text-base font-medium">{`#${table.tableCode}`}</div>
            {isSuperAdmin && (
              <AlertDialogTrigger asChild>
                <div className="w-fit">
                  <Switch checked={table.isActive} />
                </div>
              </AlertDialogTrigger>
            )}
          </CardTitle>
          <CardDescription
            className={cn(
              'text-xl font-semibold',
              table.isActive ? 'text-black01 ' : 'text-black04',
            )}
          >
            {table.type === 'CLT' ? `${table.name} Parcelas` : table.name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid w-full grid-cols-2 gap-x-8 gap-y-4">
            {items.map((i) => (
              <TableItem key={i.label} {...i} />
            ))}
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <div className="mt-4 w-full border-[1px] border-white02" />
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center justify-center gap-1">
              <History size={22} />
              <p className="text-xs font-medium">Ultima alteração: </p>
              <p className="text-xs font-medium text-black04">
                {format(table.updatedAt, 'dd/MM/yyyy')}
              </p>
            </div>
            <FormProvider {...methods}>
              <div onClick={(event) => event.stopPropagation()}>
                <div onClick={handleSettingsClick}>
                  <SettingsTable />
                </div>
              </div>
            </FormProvider>
          </div>
        </CardFooter>
      </Card>
      <EnableAndDisableCard isActive={table.isActive} id={table._id} />
    </AlertDialog>
  )
}
